"use client";

import Image from "next/image";
import { useState, useEffect, useCallback, type CSSProperties } from "react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselCounter,
  useCarousel,
} from "@/components/ui/carousel";
import { NavigationButtons } from "./NavigationButtons";
import { Skeleton } from "../ui/skeleton";
import { AccommodationImage } from "@/types/accommodations";
import { cn } from "@/lib/utils";
import { getAccommodationImageSrc } from "@/utils/getAccommodationImageSrc";

// Close ~50px + counter ~52px + thumbnails ~118px
const MAIN_IMAGE_MAX_H = "max-h-[calc(100dvh-220px)] md:max-h-[380px]";

interface MainImageSwiperProps {
  images: AccommodationImage[];
  currentIndex: number;
  onSlideChange: (index: number) => void;
}

function GalleryImage({
  src,
  alt,
  priority = false,
  onLoad,
}: {
  src: string;
  alt: string;
  priority?: boolean;
  onLoad?: () => void;
}) {
  const [isLoading, setIsLoading] = useState(true);
  const [aspectRatio, setAspectRatio] = useState(1.5); // 3/2 placeholder

  return (
    <div
      className={cn(
        "relative mx-auto w-full overflow-hidden rounded-lg",
        MAIN_IMAGE_MAX_H,
        // When max-height binds (tall/portrait), shrink width so aspect ratio holds
        "max-w-[min(100%,calc((100dvh-220px)*var(--ar)))] md:max-w-[min(100%,calc(380px*var(--ar)))]",
      )}
      style={
        {
          aspectRatio,
          "--ar": aspectRatio,
        } as CSSProperties
      }
    >
      <Image
        src={src}
        alt={alt}
        fill
        sizes="(max-width: 768px) 100vw, 942px"
        className={cn("object-contain", isLoading && "opacity-0")}
        priority={priority}
        onLoad={(e) => {
          const { naturalWidth, naturalHeight } = e.currentTarget;
          if (naturalWidth > 0 && naturalHeight > 0) {
            setAspectRatio(naturalWidth / naturalHeight);
          }
          setIsLoading(false);
          // Double rAF: wait for aspect-ratio layout before measuring
          requestAnimationFrame(() => {
            requestAnimationFrame(() => onLoad?.());
          });
        }}
      />
      {isLoading && <Skeleton className="absolute inset-0 rounded-lg" />}
    </div>
  );
}

function MainCarouselContent({
  images,
  currentIndex,
  onSlideChange,
}: MainImageSwiperProps) {
  const { api, scrollPrev, scrollNext } = useCarousel();

  const syncViewportHeight = useCallback(() => {
    if (!api) return;
    const viewport = api.rootNode();
    const slide = api.slideNodes()[api.selectedScrollSnap()];
    if (!viewport || !slide) return;
    viewport.style.height = `${slide.offsetHeight}px`;
  }, [api]);

  useEffect(() => {
    if (!api) return;

    const handleSelect = () => {
      onSlideChange(api.selectedScrollSnap());
      syncViewportHeight();
    };

    syncViewportHeight();
    api.on("select", handleSelect);
    api.on("reInit", syncViewportHeight);
    window.addEventListener("resize", syncViewportHeight);

    return () => {
      api.off("select", handleSelect);
      api.off("reInit", syncViewportHeight);
      window.removeEventListener("resize", syncViewportHeight);
    };
  }, [api, onSlideChange, syncViewportHeight]);

  useEffect(() => {
    if (!api) return;
    if (api.selectedScrollSnap() !== currentIndex) {
      api.scrollTo(currentIndex);
    }
  }, [api, currentIndex]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        scrollNext();
      } else if (e.key === "ArrowRight") {
        scrollPrev();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [scrollPrev, scrollNext]);

  return (
    <>
      {/* items-start: avoid stretching every slide to the tallest image height */}
      <CarouselContent className="items-start">
        {images.map((image, index) => (
          <CarouselItem key={index} className="pl-0">
            <GalleryImage
              src={getAccommodationImageSrc(image?.fileKey)}
              alt={`Gallery image ${index + 1}`}
              priority={index === currentIndex}
              onLoad={syncViewportHeight}
            />
          </CarouselItem>
        ))}
      </CarouselContent>

      <NavigationButtons
        showButtons={images.length > 1}
        onPrev={scrollPrev}
        onNext={scrollNext}
      />
      <CarouselCounter className="text-base bg-transparent left-1/2 -translate-x-1/2 hidden md:block" />
    </>
  );
}

export function MainImageSwiper({
  images,
  currentIndex,
  onSlideChange,
}: MainImageSwiperProps) {
  return (
    <div className="relative w-full overflow-hidden rounded-lg my-auto md:mt-0 md:mb-10">
      <Carousel
        opts={{
          loop: images.length > 1,
          align: "start",
          startIndex: currentIndex,
        }}
        dir="rtl"
        className="w-full"
      >
        <MainCarouselContent
          images={images}
          currentIndex={currentIndex}
          onSlideChange={onSlideChange}
        />
      </Carousel>
    </div>
  );
}
